import { useEffect, useSyncExternalStore } from 'react';
import { goToSection, jumpToTop } from './smoothScroll';

/*
 * A very small router: the site is one page plus a project page per project, so the History API and
 * one hook are enough. The server already falls back to the app for any unknown path (.htaccess), so
 * /projecten/<id> works as a first request too, not only as a click.
 */
const NAVIGATE = 'app:navigate';

/* A section the home page should glide to once it has rendered (set when leaving a project page). */
let pendingSection: string | null = null;

const currentPath = () => window.location.pathname.replace(/\/+$/, '') || '/';

function subscribe(callback: () => void) {
    window.addEventListener('popstate', callback);
    window.addEventListener(NAVIGATE, callback);
    return () => {
        window.removeEventListener('popstate', callback);
        window.removeEventListener(NAVIGATE, callback);
    };
}

export const usePath = () => useSyncExternalStore(subscribe, currentPath, () => '/');

export function navigate(to: string, options: { section?: string } = {}) {
    pendingSection = options.section ?? null;
    if (to !== currentPath()) {
        window.history.pushState({}, '', to);
        window.dispatchEvent(new Event(NAVIGATE));
        // A new page starts at the top (a pending section is then glided to from there).
        jumpToTop();
    }
}

/* Glide to a section; from a project page that means going home first, then gliding. */
export function goToSectionAnywhere(id: string) {
    if (document.getElementById(id)) goToSection(id);
    else navigate('/', { section: id });
}

/* Read once by the home page: the section to scroll to, if the visitor came from another page. */
export function takePendingSection(): string | null {
    const section = pendingSection;
    pendingSection = null;
    return section;
}

/* Page title, description and canonical for the current page; the home page's own values come back on leave. */
export function usePageMeta(title: string, description: string, path: string) {
    useEffect(() => {
        const previousTitle = document.title;
        const descriptionTag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
        const previousDescription = descriptionTag?.content ?? '';
        let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        const createdCanonical = !canonical;
        const previousCanonical = canonical?.href ?? '';
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
        }
        document.title = title;
        if (descriptionTag) descriptionTag.content = description;
        canonical.href = `${window.location.origin}${path}`;
        return () => {
            document.title = previousTitle;
            if (descriptionTag) descriptionTag.content = previousDescription;
            if (createdCanonical) canonical?.remove();
            else if (canonical) canonical.href = previousCanonical;
        };
    }, [title, description, path]);
}
